import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { apiGet, request } from '../lib/api/client.js';
import { apiRuntime, getAccessToken, isApiError } from '@erm/api-client';
import { serviceOf, type ServiceKind } from './machines.js';

/**
 * The admin console's data layer.
 *
 * Uses the SAME client as every Equipment Rental screen — the base URL, the
 * correlation id, the error shape and the retry predicate are all decided
 * there. TeraAgo does not get its own fetch wrapper, because a second one
 * drifts and then two products disagree about what a 409 means.
 */

export interface RateCard {
  readonly class: string;
  readonly basis: string;
  /** 'fixed' when TeraAgo sets one price for every driver (M065). */
  readonly mode?: 'fixed' | 'pilot';
  readonly baseFare: string;
  readonly includedKm: number;
  readonly includedHours: number;
  readonly perKm: string | null;
  readonly perHour: string | null;
  readonly perTon: string | null;
  readonly commissionPct: number;
  readonly freeWaitMinutes: number;
  readonly waitPerHour: string | null;
  /**
   * The band an operator's own hourly price must sit inside (driver mode,
   * hourly machines). Absent until the API serves it; null where no band is set.
   */
  readonly driverMinPerHour?: string | number | null;
  readonly driverMaxPerHour?: string | number | null;
}

/**
 * One service's slice of the dashboard: the same counts the totals carry.
 * Every field is optional, because the console is read before and after the
 * API adds them, and a missing figure is a dash rather than a zero.
 */
export interface ServiceCounts {
  readonly totalBookings?: number;
  readonly activeTrips?: number;
  readonly activeDrivers?: number;
  readonly todayBookings?: number;
  /** Bookings still looking for a driver or operator. */
  readonly searching?: number;
  readonly todayRevenue?: string;
  readonly todayCommission?: string;
  readonly byStatus?: readonly { readonly state: string; readonly count: number }[];
}

export interface DashboardData {
  readonly totalBookings: number;
  readonly activeTrips: number;
  readonly activeDrivers: number;
  readonly pendingKyc: number;
  readonly todayRevenue: string;
  readonly todayCommission: string;
  readonly fleet: {
    readonly total: number;
    readonly moving: number;
    readonly idle: number;
    readonly offline: number;
  };
  readonly revenueSeries: readonly { readonly day: string; readonly paise: number }[];
  readonly weekRevenue: string;
  readonly byStatus: readonly {
    readonly state: string;
    readonly count: number;
    readonly pct: number;
  }[];
  /** CITYLOAD_HEAVY_EQUIPMENT.md section 5. Absent on an API that predates it. */
  readonly byService?: Partial<Record<ServiceKind, ServiceCounts>>;
}

export interface DriverRow {
  readonly id: string;
  readonly name: string;
  readonly phone: string;
  readonly status: 'offline' | 'online' | 'on_trip' | 'suspended';
  readonly kyc: 'pending' | 'approved' | 'rejected';
  readonly rating: number | null;
  readonly ratingCount: number;
  readonly trips: number;
  readonly vehicle: {
    readonly registration: string;
    readonly class: string;
    readonly id?: string;
    /** False when they own it but are not on it, M069. */
    readonly assigned?: boolean;
  } | null;
  /* M069. Optional: an older API omits them. */
  readonly active?: boolean;
  readonly type?: 'individual' | 'company';
  readonly company?: { readonly id: string; readonly name: string } | null;
}

/**
 * What every booking read adds for heavy equipment (section 5). Optional
 * throughout: an older API omits them, and a goods or ride booking carries
 * nulls.
 */
export interface MachineFields {
  readonly serviceKind?: ServiceKind;
  readonly basis?: string;
  readonly workType?: string | null;
  /** numeric(4,1) on the server, which may arrive as a string. */
  readonly hoursBooked?: number | string | null;
  readonly billedHours?: number | string | null;
  /** `trip.picked_up_at` for an hourly machine, else null. */
  readonly workStartedAt?: string | null;
  readonly workMinutes?: number | null;
  readonly perHour?: string | null;
}

export interface BookingRow extends MachineFields {
  readonly id: string;
  readonly reference: string;
  readonly customer: string;
  readonly driver: string | null;
  readonly vehicleClass: string;
  readonly from: string;
  readonly to: string;
  readonly state: string;
  readonly fare: string;
  readonly commission: string | null;
  readonly at: string;
}

export interface KycRow {
  readonly driverId: string;
  readonly name: string;
  readonly phone: string;
  readonly vehicleClass: string | null;
  readonly submittedAt: string;
  readonly documents: readonly {
    readonly kind: string;
    readonly state: string;
    /** Whether the driver uploaded a photo the reviewer can open. */
    readonly hasPhoto?: boolean;
    /** The reason given when this document was rejected. */
    readonly note?: string | null;
  }[];
  readonly complete: boolean;
}

export interface WalletRow {
  readonly driverId: string;
  readonly driver: string;
  readonly phone: string;
  readonly balance: string;
  readonly balancePaise: number;
  readonly pending: string;
  readonly lastPayout: string | null;
}

export interface SettlementRow {
  readonly id: string;
  readonly reference: string;
  readonly driver: string;
  readonly state: 'queued' | 'processing' | 'paid' | 'failed';
  readonly amount: string;
  readonly method: string;
  readonly destination: string;
  readonly failureReason: string | null;
  readonly paidAt: string | null;
  readonly createdAt: string;
}

export interface PaymentRow {
  readonly id: string;
  readonly booking: string;
  readonly customer: string;
  readonly method: string;
  readonly state: 'pending' | 'captured' | 'failed' | 'refunded';
  readonly amount: string;
  readonly failureReason: string | null;
  readonly at: string;
}

export interface ComplaintRow {
  readonly id: string;
  readonly reference: string;
  readonly booking: string | null;
  readonly category: 'driver' | 'payment' | 'customer' | 'goods';
  readonly priority: 'normal' | 'high';
  readonly state: 'open' | 'investigating' | 'resolved' | 'dismissed';
  readonly raisedByName: string;
  readonly againstName: string | null;
  readonly summary: string;
  readonly resolution: string | null;
  readonly createdAt: string;
}

export interface PromoRow {
  readonly code: string;
  readonly kind: 'flat' | 'percent';
  readonly value: string;
  readonly minFare: string;
  readonly maxDiscount: string | null;
  readonly used: number;
  readonly cap: number;
  readonly discountGiven: string;
  readonly state: 'live' | 'scheduled' | 'expired';
  readonly endsAt: string;
}

export interface NotificationRow {
  readonly id: string;
  readonly kind: string;
  readonly state: 'queued' | 'sent' | 'failed' | 'skipped';
  readonly title: string;
  readonly body: string;
  readonly audience: string | null;
  readonly recipients: number;
  readonly sendAfter: string;
  readonly sentAt: string | null;
  readonly createdAt: string;
}

export interface VehicleRow {
  readonly id: string;
  readonly registration: string;
  readonly class: string;
  readonly owner: string;
  readonly capacity: string | null;
  readonly insuranceExpires: string;
  readonly pucExpires: string;
  readonly status: 'active' | 'expiring' | 'expired';
}

export interface TrackedDriver {
  readonly driverId: string;
  readonly name: string;
  readonly status: string;
  readonly vehicleClass: string | null;
  readonly lat: number;
  readonly lng: number;
  readonly headingDeg: number | null;
  readonly speedKph: number | null;
  readonly recordedAt: string;
}

interface Envelope<T> {
  readonly data: T;
}

const KEY = ['cityload'] as const;

/**
 * Live data refreshes on an interval; the rest does not.
 *
 * A dispatcher watching the tracking map needs it to move. A rate card does
 * not change while they are looking at it, and polling it every ten seconds is
 * a query per user per ten seconds for a row that changes twice a year.
 */
const LIVE = { refetchInterval: 10_000, staleTime: 5_000 } as const;

export function useDashboard(): UseQueryResult<DashboardData> {
  return useQuery({
    queryKey: [...KEY, 'dashboard'],
    queryFn: async () => (await apiGet<Envelope<DashboardData>>('/v1/cityload/dashboard')).data,
    ...LIVE,
  });
}

/**
 * The widest page the list routes will return. `limit` is capped at 200 on the
 * server, and asking for fewer only shrinks what a filter can reach.
 */
export const LIST_LIMIT = 200;

/** A query string from the parameters that are actually set. */
function params(values: Readonly<Record<string, string | number | undefined>>): string {
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined) q.set(key, String(value));
  }
  const text = q.toString();
  return text === '' ? '' : `?${text}`;
}

/**
 * Drivers, filtered ON THE SERVER.
 *
 * `GET /v1/cityload/drivers` has always accepted `status`, `kyc` and `limit`,
 * and this passed none of them: it fetched fifty and the page filtered those
 * fifty. "Suspended" answered "which of the fifty most recently joined are
 * suspended" while reading as "who is suspended".
 *
 * The filter is part of the query key, so each choice is its own cached answer
 * and switching back is instant.
 */
export function useDrivers(
  filter: {
    readonly status?: string | undefined;
    readonly kyc?: string | undefined;
    readonly service?: ServiceKind | null | undefined;
    /* M069: individual or company driver, and whether a machine is under them. */
    readonly type?: 'individual' | 'company' | undefined;
    readonly assigned?: 'yes' | 'no' | undefined;
  } = {},
): UseQueryResult<readonly DriverRow[]> {
  const service = filter.service ?? undefined;
  return useQuery({
    queryKey: [
      ...KEY,
      'drivers',
      filter.status ?? 'all',
      filter.kyc ?? 'all',
      service ?? 'all',
      filter.type ?? 'all',
      filter.assigned ?? 'all',
    ],
    queryFn: () =>
      byService<DriverRow>(
        '/v1/cityload/drivers',
        {
          status: filter.status,
          kyc: filter.kyc,
          type: filter.type,
          assigned: filter.assigned,
          limit: LIST_LIMIT,
        },
        service,
        (d) => serviceOf(d.vehicle?.class) === service,
      ),
  });
}

/**
 * A list read narrowed to one service.
 *
 * `?service=` is sent, and the rows are ALSO narrowed here by class, so the
 * tab is right whichever side filters. An API that predates the parameter
 * refuses unknown query keys with a 400; that one refusal is retried without
 * it rather than turned into an empty table.
 */
async function byService<Row>(
  path: string,
  values: Readonly<Record<string, string | number | undefined>>,
  service: ServiceKind | undefined,
  keep: (row: Row) => boolean,
): Promise<readonly Row[]> {
  if (service === undefined) {
    return (await apiGet<Envelope<Row[]>>(`${path}${params(values)}`)).data;
  }
  try {
    const rows = (await apiGet<Envelope<Row[]>>(`${path}${params({ ...values, service })}`)).data;
    return rows.filter(keep);
  } catch (error) {
    if (!isApiError(error) || (error.status !== 400 && error.status !== 422)) throw error;
    const rows = (await apiGet<Envelope<Row[]>>(`${path}${params(values)}`)).data;
    return rows.filter(keep);
  }
}

/**
 * Bookings, filtered ON THE SERVER by state.
 *
 * Same defect as drivers: the route accepts `state` and `limit` and this sent
 * neither, so "Searching" found only the searching loads among the fifty most
 * recent. A booking stuck since yesterday, which is exactly the one an operator
 * opens this filter to find, was not in the list at all.
 *
 * Called with no argument by the dashboard, which wants the most recent loads
 * of every state and gets them exactly as before.
 */
export function useBookings(
  filter: {
    readonly state?: string | undefined;
    readonly service?: ServiceKind | null | undefined;
  } = {},
): UseQueryResult<readonly BookingRow[]> {
  const service = filter.service ?? undefined;
  return useQuery({
    queryKey: [...KEY, 'bookings', filter.state ?? 'all', service ?? 'all'],
    queryFn: () =>
      byService<BookingRow>(
        '/v1/cityload/bookings',
        { state: filter.state, limit: LIST_LIMIT },
        service,
        (b) => (b.serviceKind ?? serviceOf(b.vehicleClass)) === service,
      ),
    ...LIVE,
  });
}

export function useKycQueue(): UseQueryResult<readonly KycRow[]> {
  return useQuery({
    queryKey: [...KEY, 'kyc'],
    queryFn: async () => (await apiGet<Envelope<KycRow[]>>('/v1/cityload/kyc')).data,
  });
}

export function useVehicles(): UseQueryResult<readonly VehicleRow[]> {
  return useQuery({
    queryKey: [...KEY, 'vehicles'],
    queryFn: async () => (await apiGet<Envelope<VehicleRow[]>>('/v1/cityload/vehicles')).data,
  });
}

export function useTracking(): UseQueryResult<readonly TrackedDriver[]> {
  return useQuery({
    queryKey: [...KEY, 'tracking'],
    queryFn: async () => (await apiGet<Envelope<TrackedDriver[]>>('/v1/cityload/tracking')).data,
    ...LIVE,
  });
}

export function useComplaints(): UseQueryResult<{
  readonly rows: readonly ComplaintRow[];
  readonly counts: Record<string, number>;
}> {
  return useQuery({
    queryKey: [...KEY, 'complaints'],
    queryFn: async () => {
      const result = await apiGet<
        Envelope<ComplaintRow[]> & { meta: { counts: Record<string, number> } }
      >('/v1/cityload/complaints');
      return { rows: result.data, counts: result.meta.counts };
    },
  });
}

export function useNotifications(): UseQueryResult<{
  readonly rows: readonly NotificationRow[];
  readonly reachableDevices: number;
  readonly pushConfigured: boolean;
}> {
  return useQuery({
    queryKey: [...KEY, 'notifications'],
    queryFn: async () => {
      const result = await apiGet<
        Envelope<NotificationRow[]> & {
          meta: { reachableDevices: number; pushConfigured: boolean };
        }
      >('/v1/cityload/notifications');
      return {
        rows: result.data,
        reachableDevices: result.meta.reachableDevices,
        pushConfigured: result.meta.pushConfigured,
      };
    },
  });
}

/**
 * Send a broadcast to drivers or customers.
 *
 * "Compose" was a dead button beside a working endpoint. The audience is
 * required because the API requires it — a broadcast with no audience is one
 * nobody can explain afterwards.
 */
export function useComposeNotification(): ReturnType<
  typeof useMutation<
    { id: string; wouldReach: number; note: string },
    Error,
    { title: string; message: string; audience: string }
  >
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const result = await request<Envelope<{ id: string; wouldReach: number; note: string }>>(
        '/v1/cityload/notifications',
        { method: 'POST', body },
      );
      return result.data.data;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

/** What one pass of the sender did. Returned verbatim — including the note. */
export interface DrainReport {
  readonly configured: boolean;
  readonly considered: number;
  readonly sent: number;
  readonly failed: number;
  readonly skipped: number;
  readonly tokensDisabled: number;
  readonly note: string | null;
}

/**
 * Send everything that is queued and due.
 *
 * A button rather than only a timer, because the person who just composed a
 * surge alert wants it gone now and wants to SEE what happened to it. The
 * report comes back whole — including "nothing was sent, and here is why".
 */
export function useDrainNotifications(): ReturnType<typeof useMutation<DrainReport, Error, void>> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const result = await request<Envelope<DrainReport>>('/v1/cityload/notifications/drain', {
        method: 'POST',
        body: {},
      });
      return result.data.data;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

/** What `POST /v1/cityload/promos` takes. Paise, because the API speaks paise. */
export interface NewPromo {
  readonly code: string;
  readonly kind: 'flat' | 'percent';
  /** Rupees off for `flat`, or the percentage for `percent`. */
  readonly value: number;
  readonly minFarePaise: number;
  readonly maxDiscountPaise?: number;
  readonly cap: number;
  readonly endsAt: string;
}

/**
 * Create a promo code.
 *
 * `POST /v1/cityload/promos` has existed the whole time and no client called
 * it: the Promos page listed codes, counted their redemptions and offered no
 * way to make one, with nothing on screen saying why. An admin could watch a
 * promotion and not run one.
 */
export function useCreatePromo(): ReturnType<
  typeof useMutation<{ code: string }, Error, NewPromo>
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (promo: NewPromo) => {
      const result = await request<Envelope<{ code: string }>>('/v1/cityload/promos', {
        method: 'POST',
        body: promo,
      });
      return { code: result.data.data.code };
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * ONE BOOKING, in full.
 *
 * `GET /v1/cityload/bookings/:bookingId` has existed the whole time with no
 * caller: the admin Bookings table had no row action at all, so an operator
 * answering "where is CLD12548" could read one line of it and nothing else.
 *
 * The OTPs are deliberately absent from the payload. A dispatcher who can read
 * the codes can close a job neither party attended, which is the one thing the
 * two codes exist to prevent.
 */
export interface AdminBookingDetail extends MachineFields {
  readonly id: string;
  readonly reference: string;
  readonly state: string;
  /** The FULL number. This endpoint is cl_admin, and support has to ring back. */
  readonly customer: { readonly name: string; readonly phone: string };
  readonly vehicleClass: string;
  readonly pickup: { readonly label: string; readonly lat: number; readonly lng: number };
  readonly drop: { readonly label: string; readonly lat: number; readonly lng: number };
  readonly goods: string | null;
  readonly weightBand: string | null;
  readonly fare: {
    readonly quoted: string | null;
    /** Null until the trip settles. Not zero: nobody has been charged yet. */
    readonly final: string | null;
    readonly commission: string | null;
    readonly surge: number;
  };
  /** Null while nobody has taken the load, which is an ordinary first minute. */
  readonly driver: {
    readonly name: string;
    readonly phone: string;
    readonly registration: string | null;
    readonly acceptedAt: string;
    readonly arrivedAt: string | null;
    readonly pickedUpAt: string | null;
    readonly deliveredAt: string | null;
    readonly distanceKm: number | null;
    readonly waitingMinutes: number;
  } | null;
  readonly cancelledBy: string | null;
  readonly cancellationReason: string | null;
  readonly createdAt: string;
}

export function useAdminBooking(bookingId: string | null): UseQueryResult<AdminBookingDetail> {
  return useQuery({
    queryKey: [...KEY, 'booking', bookingId],
    enabled: bookingId !== null && bookingId !== '',
    queryFn: async () =>
      (await apiGet<Envelope<AdminBookingDetail>>(`/v1/cityload/bookings/${bookingId ?? ''}`)).data,
  });
}

export function usePromos(): UseQueryResult<readonly PromoRow[]> {
  return useQuery({
    queryKey: [...KEY, 'promos'],
    queryFn: async () => (await apiGet<Envelope<PromoRow[]>>('/v1/cityload/promos')).data,
  });
}

/** Close a complaint. The resolution is required — see the endpoint. */
export function useCloseComplaint(): ReturnType<
  typeof useMutation<
    { state: string },
    Error,
    { id: string; outcome: 'resolved' | 'dismissed'; resolution: string }
  >
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }) => {
      const result = await request<Envelope<{ state: string }>>(
        `/v1/cityload/complaints/${id}/close`,
        { method: 'POST', body },
      );
      return { state: result.data.data.state };
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

export function useWallets(): UseQueryResult<readonly WalletRow[]> {
  return useQuery({
    queryKey: [...KEY, 'wallets'],
    queryFn: async () => (await apiGet<Envelope<WalletRow[]>>('/v1/cityload/wallets')).data,
  });
}

export function useSettlements(): UseQueryResult<readonly SettlementRow[]> {
  return useQuery({
    queryKey: [...KEY, 'settlements'],
    queryFn: async () => (await apiGet<Envelope<SettlementRow[]>>('/v1/cityload/settlements')).data,
  });
}

export function usePayments(): UseQueryResult<readonly PaymentRow[]> {
  return useQuery({
    queryKey: [...KEY, 'payments'],
    queryFn: async () => (await apiGet<Envelope<PaymentRow[]>>('/v1/cityload/payments')).data,
  });
}

/**
 * Queue a payout for a driver.
 *
 * Nothing reaches a bank: no payment provider is connected, so this records a
 * `queued` settlement an operator must then settle. The button says so.
 */
export function usePayout(): ReturnType<
  typeof useMutation<
    { reference: string },
    Error,
    { driverId: string; method: 'upi' | 'card'; destination: string }
  >
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ driverId, ...body }) => {
      const result = await request<Envelope<{ reference: string }>>(
        `/v1/cityload/drivers/${driverId}/payout`,
        { method: 'POST', body },
      );
      return { reference: result.data.data.reference };
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Close a queued payout — mark it paid, or mark it failed with a reason.
 *
 * THE OTHER HALF OF `usePayout`, which was missing. That one queues a
 * settlement and no mutation in this file ever closed one, so the console
 * could put money into the queue and had no way to take it out again: every
 * payout an operator raised sat at `queued` forever, the driver's balance
 * stayed held out of `withdrawable`, and the only route to a paid state was
 * somebody opening psql.
 *
 * `POST /v1/cityload/settlements/:id/settle` has existed the whole time. This
 * is the call the button needed.
 *
 * A FAILURE MUST CARRY A REASON. The server enforces it with a 422 and the
 * form requires it too — deliberately both. The form stops the ordinary
 * mistake; the 422 stops a caller that is not this form. The reason is not
 * bookkeeping: it is what the driver is told about money they were expecting.
 */
export function useSettlePayout(): ReturnType<
  typeof useMutation<
    { id: string; state: 'paid' | 'failed' },
    Error,
    {
      settlementId: string;
      outcome: 'paid' | 'failed';
      providerRef?: string;
      failureReason?: string;
    }
  >
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ settlementId, ...body }) => {
      const result = await request<Envelope<{ id: string; state: 'paid' | 'failed' }>>(
        `/v1/cityload/settlements/${settlementId}/settle`,
        { method: 'POST', body },
      );
      return result.data.data;
    },
    /* The whole TeraAgo key, not just the settlements list: paying one moves
       the driver's wallet balance and the money headline figures too, and a
       console showing a paid settlement above a stale balance is the screen an
       operator pays somebody twice from. */
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRateCards(): UseQueryResult<readonly RateCard[]> {
  return useQuery({
    queryKey: [...KEY, 'rate-cards'],
    queryFn: async () => (await apiGet<Envelope<RateCard[]>>('/v1/cityload/rate-cards')).data,
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Approve a driver's KYC.
 *
 * The server refuses an incomplete file with a 409 and the UI disables the
 * button for the same case — deliberately both. The button stops the ordinary
 * mistake; the 409 stops the race where a second operator rejects a document
 * between this page rendering and the click landing.
 */
/**
 * Offer a waiting load to drivers now, rather than on the next sweep.
 *
 * `POST /bookings/:bookingId/dispatch` has existed the whole time and no client
 * called it. The route places a load and the sweeper picks up anything that did
 * not go out, so in the ordinary case nobody needs this. It is for the case an
 * operator is looking at: a customer on the phone asking why nothing has
 * happened, a driver who has just come online next door, a load that has sat in
 * "searching" long enough that somebody wants to push it.
 *
 * Answers who it went to. An empty list is not a failure: nobody eligible is in
 * range, and the booking stays alive and keeps being offered.
 */
export function useDispatchBooking(): ReturnType<
  typeof useMutation<
    { offered: readonly { driver: string }[]; reason: string | null },
    Error,
    string
  >
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (bookingId: string) => {
      const result = await request<Envelope<{ offered: { driver: string }[]; reason?: string }>>(
        `/v1/cityload/bookings/${bookingId}/dispatch`,
        { method: 'POST' },
      );
      return {
        offered: result.data.data.offered,
        reason: result.data.data.reason ?? null,
      };
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

export function useApproveKyc(): ReturnType<
  typeof useMutation<{ driverId: string }, Error, string>
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (driverId: string) => {
      const result = await request<Envelope<{ driverId: string; kyc: string }>>(
        `/v1/cityload/kyc/${driverId}/approve`,
        { method: 'POST' },
      );
      return { driverId: result.data.data.driverId };
    },
    onSuccess: () => {
      // The queue, the driver list and the dashboard's pending count all
      // change together, so all three are invalidated together.
      void client.invalidateQueries({ queryKey: KEY });
    },
  });
}

/**
 * Reject a driver's KYC, with the reason the schema requires.
 *
 * Approve was wired and reject was not, so an operator could only ever say
 * yes — a queue with one exit is not a review, it is a rubber stamp.
 *
 * The reason is mandatory here because it is mandatory in the database
 * (`kyc_document_rejection_has_reason`). A driver told only "rejected" cannot
 * know what to fix, and phones support instead.
 */
export function useRejectKyc(): ReturnType<
  typeof useMutation<{ driverId: string }, Error, { driverId: string; reason: string }>
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ driverId, reason }) => {
      const result = await request<Envelope<{ driverId: string }>>(
        `/v1/cityload/kyc/${driverId}/reject`,
        { method: 'POST', body: { reason } },
      );
      return { driverId: result.data.data.driverId };
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Approve ONE document. The server verifies the driver when it is the last one
 * their vehicle needs, and says so in `driverVerified`.
 */
export function useApproveDocument(): ReturnType<
  typeof useMutation<{ driverVerified: boolean }, Error, { driverId: string; kind: string }>
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ driverId, kind }) => {
      const result = await request<Envelope<{ driverVerified: boolean }>>(
        `/v1/cityload/kyc/${driverId}/documents/${kind}/approve`,
        { method: 'POST' },
      );
      return { driverVerified: result.data.data.driverVerified };
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

/** Reject ONE document, with the reason the driver will read. */
export function useRejectDocument(): ReturnType<
  typeof useMutation<void, Error, { driverId: string; kind: string; reason: string }>
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ driverId, kind, reason }) => {
      await request(`/v1/cityload/kyc/${driverId}/reject`, {
        method: 'POST',
        body: { reason, kinds: [kind] },
      });
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * A document photo as an object URL the page can put in an `<img>`.
 *
 * The route needs the admin's bearer token, which an `<img src>` cannot send,
 * so the bytes are fetched and handed over as a blob. The caller revokes it.
 */
export async function documentImageUrl(driverId: string, kind: string): Promise<string> {
  const token = getAccessToken();
  const response = await fetch(
    `${apiRuntime().baseUrl}/v1/cityload/kyc/${driverId}/documents/${kind}/image`,
    { headers: token === null || token === undefined ? {} : { authorization: `Bearer ${token}` } },
  );
  if (!response.ok) throw new Error(`photo ${response.status}`);
  return URL.createObjectURL(await response.blob());
}

/* ---------------------------------------------- price controls (M065) */

export interface PriceCard {
  readonly class: string;
  readonly zoneId: string | null;
  readonly mode: 'fixed' | 'driver';
  readonly basis: string;
  readonly baseFare: number;
  readonly includedKm: number;
  readonly includedHours: number;
  readonly perKm: number;
  readonly perHour: number;
  readonly perTon: number;
  readonly minFare: number;
  readonly freeWaitMinutes: number;
  readonly waitPerHour: number;
  readonly commissionPct: number;
  readonly driverMinPerKm: number | null;
  readonly driverMaxPerKm: number | null;
  readonly driverMinBase: number | null;
  readonly driverMaxBase: number | null;
  /**
   * The per-hour band for an hourly machine in driver mode (M067,
   * `driver_min_per_hour_paise` / `driver_max_per_hour_paise`), in rupees like
   * the rest of this card. ABSENT, not null, on an API that does not serve it
   * yet, and that absence is what tells the editor it cannot send them.
   */
  readonly driverMinPerHour?: number | null;
  readonly driverMaxPerHour?: number | null;
  readonly effectiveFrom: string;
  readonly versions: number;
}

export interface PriceZone {
  readonly id: string;
  readonly name: string;
  readonly lat: number;
  readonly lng: number;
  readonly radiusKm: number;
  readonly active: boolean;
}

export interface PricingView {
  readonly areas: readonly { id: string; name: string; state: string; live: boolean }[];
  readonly area: { id: string; name: string; state: string; live: boolean } | null;
  readonly classes: readonly string[];
  readonly zones: readonly PriceZone[];
  readonly cards: readonly PriceCard[];
}

export function usePricing(areaId: string | null): UseQueryResult<PricingView> {
  return useQuery({
    queryKey: [...KEY, 'pricing', areaId ?? 'default'],
    queryFn: async () =>
      (
        await apiGet<Envelope<PricingView>>(
          `/v1/cityload/pricing${areaId === null ? '' : `?areaId=${areaId}`}`,
        )
      ).data,
  });
}

export type PriceCardInput = Omit<
  PriceCard,
  'basis' | 'effectiveFrom' | 'versions' | 'class' | 'zoneId'
> & {
  readonly areaId: string;
  readonly zoneId: string | null;
  readonly class: string;
  readonly applyToState: boolean;
};

export function useSavePriceCard(): ReturnType<
  typeof useMutation<{ cities: number }, Error, PriceCardInput>
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const result = await request<Envelope<{ cities: number }>>('/v1/cityload/pricing/cards', {
        method: 'POST',
        body,
      });
      return result.data.data;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAddZone(): ReturnType<
  typeof useMutation<
    { id: string },
    Error,
    { areaId: string; name: string; lat: number; lng: number; radiusKm: number }
  >
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      (await request<Envelope<{ id: string }>>('/v1/cityload/zones', { method: 'POST', body })).data
        .data,
    onSuccess: () => void client.invalidateQueries({ queryKey: [...KEY, 'pricing'] }),
  });
}

export function useSetZoneActive(): ReturnType<
  typeof useMutation<void, Error, { zoneId: string; active: boolean }>
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ zoneId, active }) => {
      await request(`/v1/cityload/zones/${zoneId}/active`, { method: 'POST', body: { active } });
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: [...KEY, 'pricing'] }),
  });
}

/* ---------------------------------------------- driver-set rates */

/**
 * One price a driver has stated, as the API reads it back.
 *
 * EVERY AMOUNT IS A STRING the server has already formatted. The console
 * prints them and does nothing else to them — a figure re-rounded in the
 * browser is a rate the admin approves and the fare engine then disagrees
 * with, on a trip somebody has already paid for.
 *
 * The nullable ones are null where the driver does not charge on that footing
 * at all: a JCB hired by the hour has no per-kilometre rate, and that is a
 * fact about the machine rather than a missing number.
 */
export interface DriverRate {
  readonly id: string;
  readonly vehicleClass: string;
  readonly basis: string;
  readonly baseFare: string;
  readonly includedKm: number;
  readonly includedHours: number;
  readonly perKm: string | null;
  readonly perHour: string | null;
  readonly perTon: string | null;
  readonly freeWaitMinutes: number;
  readonly waitPerHour: string | null;
  readonly reviewState: 'pending' | 'approved' | 'rejected';
  readonly submittedAt: string;
  readonly reviewedAt: string | null;
  /** The admin's own words, carried back to the driver verbatim. */
  readonly reviewNote: string | null;
}

/** A row of the pending queue: the price, plus who is waiting behind it. */
export interface RateApprovalRow extends DriverRate {
  readonly driverId: string;
  readonly driverName: string;
  readonly driverPhone: string;
  readonly kycState: 'pending' | 'approved' | 'rejected';
  /**
   * Null for a driver with no service area — they have not finished
   * registering, and approving their price would not put them on the road.
   */
  readonly area: string | null;
}

/** One driver, whole: what they charge AND what they have sent us. */
export interface RateApprovalDetail {
  readonly driver: {
    readonly id: string;
    readonly name: string;
    readonly phone: string;
    readonly status: string;
    readonly kycState: 'pending' | 'approved' | 'rejected';
    readonly tripsCompleted: number;
    /** Null, never 0 — a new driver has no rating, and 0 reads as a dreadful one. */
    readonly rating: number | null;
    readonly area: string | null;
  };
  /**
   * Only the documents that EXIST. A kind missing from this list was never
   * sent, which the screen has to say in words rather than leave as a gap.
   */
  readonly documents: readonly {
    readonly kind: string;
    readonly state: 'pending' | 'approved' | 'rejected';
    readonly note: string | null;
    readonly reviewedAt: string | null;
    readonly uploadedAt: string;
  }[];
  readonly rates: readonly DriverRate[];
  /** What the platform takes in this driver's city, per class. */
  readonly commission: readonly { readonly vehicleClass: string; readonly pct: number }[];
}

/** Everything waiting on a decision, oldest first — a queue, not a search. */
export function useRateApprovals(): UseQueryResult<readonly RateApprovalRow[]> {
  return useQuery({
    queryKey: [...KEY, 'rate-approvals'],
    queryFn: async () =>
      (await apiGet<Envelope<RateApprovalRow[]>>('/v1/cityload/rate-approvals')).data,
  });
}

/**
 * One driver's documents, prices and commission, in ONE request.
 *
 * Deliberately not three: the admin decides once, and a screen that fetched
 * the price and the documents separately could paint half a decision while the
 * other half was still in flight.
 */
export function useRateApproval(driverId: string): UseQueryResult<RateApprovalDetail> {
  return useQuery({
    queryKey: [...KEY, 'rate-approvals', driverId],
    queryFn: async () =>
      (await apiGet<Envelope<RateApprovalDetail>>(`/v1/cityload/rate-approvals/${driverId}`)).data,
    // A URL with no id in it must not fetch `/rate-approvals/undefined`.
    enabled: driverId !== '',
  });
}

/**
 * A price the admin just approved, and what actually happened to the driver's
 * KYC as a result.
 *
 * `driverVerified` and `kycNote` WERE MISSING FROM THIS TYPE, though the route
 * has always returned them: approving a price clears KYC only when all six
 * documents are already approved (see the route's own comment on "THAT LAST
 * CONDITION WAS MISSING AND IT WAS A HOLE THROUGH THE WHOLE GATE"), and the
 * confirmation dialog on this screen said unconditionally that approving
 * "also approves their KYC and puts them on the road" — true for a complete
 * file, false for an incomplete one, and the response that says which was
 * being discarded by a type that did not declare the fields.
 */
export interface RateApproved extends DriverRate {
  readonly driverVerified: boolean;
  readonly kycNote: string | null;
}

/**
 * Approve a price — the one action that puts a driver on the road.
 *
 * It also clears their KYC, server-side and in the same transaction, BUT ONLY
 * WHEN ALL SIX DOCUMENTS ARE ALREADY APPROVED. The screen says so beside the
 * button, because an admin who believes they are only agreeing a number is
 * signing off the documents as well — and the response says whether that
 * actually happened this time, so the screen can print the truth rather than
 * leave the admin to discover a driver never comes online.
 */
export function useApproveRate(): ReturnType<typeof useMutation<RateApproved, Error, string>> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (rateId: string) => {
      const result = await request<Envelope<RateApproved>>(
        `/v1/cityload/rate-approvals/${rateId}/approve`,
        { method: 'POST' },
      );
      return result.data.data;
    },
    // The queue, this driver, the driver list and the dashboard's pending KYC
    // count all move together, so all of them are invalidated together.
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Reject a price, with the reason the API insists on (three characters, and
 * the driver is shown it).
 *
 * A driver told only "rejected" resubmits the same number, and the admin
 * reviews it twice.
 */
export function useRejectRate(): ReturnType<
  typeof useMutation<DriverRate, Error, { rateId: string; reason: string }>
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ rateId, reason }) => {
      const result = await request<Envelope<DriverRate>>(
        `/v1/cityload/rate-approvals/${rateId}/reject`,
        { method: 'POST', body: { reason } },
      );
      return result.data.data;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * The props every TeraAgo page hands its shell.
 *
 * The sidebar badge and the page beneath it must agree. They did not: the
 * badge was a hard-coded 12 above a KYC queue of 2, which is the kind of
 * mismatch a reader notices before anything else on the screen.
 *
 * Every count here is `undefined` until its query answers, and `undefined`
 * renders NO badge. A zero would be a claim ("nothing is waiting") made on
 * behalf of a request that has not come back.
 */
export function useShell(): {
  readonly badges: { readonly kyc?: number | undefined; readonly complaints?: number | undefined };
  readonly notifications: number | undefined;
} {
  const dashboard = useDashboard();
  const complaints = useComplaints();
  // Open AND investigating: both are a dispatcher's problem, and a badge that
  // counted only `open` would drop as soon as somebody started looking at one.
  const openComplaints =
    complaints.data === undefined
      ? undefined
      : (complaints.data.counts['open'] ?? 0) + (complaints.data.counts['investigating'] ?? 0);
  return {
    badges: { kyc: dashboard.data?.pendingKyc, complaints: openComplaints },
    /*
      THE BELL HAS NOTHING TO COUNT, so it counts nothing.

      This returned a hard-coded 8 on all seventeen screens, above a
      Notifications page that could be listing three rows or none. Counting it
      properly means fetching the notification list on every page, which is a
      query per dispatcher per section for a number nobody acts on — so the
      bell is a link until there is a cheap count to put in it.
    */
    notifications: undefined,
  };
}

/**
 * Download a CSV report.
 *
 * NOT through `request`: the shared client parses every response as JSON, and
 * a CSV body would throw on the first character. This uses `fetch` directly
 * and reads the text.
 *
 * The Authorization header is still required — a plain `<a download>` sends
 * none and is refused with a 401 the browser renders as a broken download, so
 * the token is attached explicitly.
 *
 * The blob goes to a temporary anchor because that is the only way to name a
 * file from JavaScript, and the object URL is revoked straight after, or the
 * page holds the whole export in memory until it is reloaded.
 */
export async function downloadReport(report: string): Promise<void> {
  const token = getAccessToken();
  const response = await fetch(`${apiRuntime().baseUrl}/v1/cityload/reports/${report}`, {
    headers: token === null ? {} : { authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(`Report failed: ${response.status}`);
  }

  const csv = await response.text();
  const stamp = new Date().toISOString().slice(0, 10);
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `cityload-${report}-${stamp}.csv`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** A city TeraAgo has declared, whether or not it is open yet. */
export interface AreaRow {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly state: string;
  readonly live: boolean;
  readonly openedAt: string | null;
  readonly radiusKm: number;
  readonly centre: { readonly lat: number; readonly lng: number };
  readonly drivers: number;
  readonly bookings: number;
  readonly priced: boolean;
  readonly dispatch: {
    readonly searchRadiusKm: number;
    readonly broadcastSize: number;
    readonly offerWindowSeconds: number;
  } | null;
}

/**
 * Every declared city, with its real driver and booking counts.
 *
 * The Service Areas page rendered a fixture before this existed — invented
 * areas with invented pincodes — while the database held the real sixteen. A
 * roadmap screen that is wrong about which cities exist is worse than none,
 * because somebody plans against it.
 */
export function useAreas(): UseQueryResult<{
  readonly rows: readonly AreaRow[];
  readonly open: number;
  readonly declared: number;
  readonly states: number;
}> {
  return useQuery({
    queryKey: [...KEY, 'areas'],
    queryFn: async () => {
      const result = await apiGet<
        Envelope<AreaRow[]> & { meta: { open: number; declared: number; states: number } }
      >('/v1/cityload/areas');
      return { rows: result.data, ...result.meta };
    },
  });
}

/* ------------------------------------------------------- the admin lists */

export interface CustomerRow {
  readonly id: string;
  readonly phone: string;
  readonly name: string;
  readonly city: string | null;
  readonly bookings: number;
  readonly delivered: number;
  readonly cancelled: number;
  /** Null — not "₹0" — for a customer with nothing delivered yet. */
  readonly spent: string | null;
  readonly firstBooked: string;
  readonly lastBooked: string;
}

/** Everyone who has booked, derived from the bookings. There is no customer table. */
export function useCustomers(): UseQueryResult<readonly CustomerRow[]> {
  return useQuery({
    queryKey: [...KEY, 'customers'],
    queryFn: async () => (await apiGet<Envelope<CustomerRow[]>>('/v1/cityload/customers')).data,
  });
}

export interface AdminUserRow {
  readonly id: string;
  readonly phone: string;
  readonly status: string;
  readonly capabilities: readonly string[];
  readonly isOperator: boolean;
  readonly createdAt: string;
  readonly grantedAt: string;
}

/** Accounts holding a TeraAgo capability. Nothing from `pii.*` is returned. */
export function useAdminUsers(): UseQueryResult<readonly AdminUserRow[]> {
  return useQuery({
    queryKey: [...KEY, 'admin-users'],
    queryFn: async () => (await apiGet<Envelope<AdminUserRow[]>>('/v1/cityload/admin-users')).data,
  });
}

export interface SettingRow {
  readonly areaId: string;
  readonly code: string;
  readonly city: string;
  readonly state: string;
  readonly live: boolean;
  readonly coversKm: number;
  /** Null means the city cannot dispatch — reported, never defaulted away. */
  readonly policy: {
    readonly broadcastSize: number;
    readonly offerWindowSeconds: number;
    readonly searchRadiusKm: number;
    readonly scheduleLeadMinutes: number;
  } | null;
}

/** The dispatch policy per city — per service area since M056. */
export interface Integrations {
  /** Null means NOT CONNECTED. The console must say so rather than name it. */
  readonly payments: string | null;
  readonly push: string | null;
  readonly sms: string | null;
  readonly maps: string | null;
}

export function useSettings(): UseQueryResult<{
  readonly rows: readonly SettingRow[];
  readonly declared: number;
  readonly live: number;
  readonly missingPolicy: number;
  readonly integrations: Integrations;
}> {
  return useQuery({
    queryKey: [...KEY, 'settings'],
    queryFn: async () => {
      const result = await apiGet<
        Envelope<SettingRow[]> & {
          meta: {
            declared: number;
            live: number;
            missingPolicy: number;
            integrations: Integrations;
          };
        }
      >('/v1/cityload/settings');
      return { rows: result.data, ...result.meta };
    },
  });
}

/* ==========================================================================
   WHICH HALVES OF TERAAGO ARE OPEN FOR BUSINESS

   `ride` is off because the company has no aggregator licence: MV Aggregator
   Guidelines 2025 clause 5 prices one at Rs 5,00,000 and clauses 6, 11 and
   14.12 add a deposit, driver insurance and a 24x7 control room on top.
   Equipment work and self-drive hire carry no passenger and need none of it.

   The console is not the enforcement — triggers in M085 refuse the insert — so
   read these as "what is being offered", not "what is possible".
   ========================================================================== */

export type ServiceChannel = 'ride' | 'goods' | 'equipment' | 'rental' | 'driver';

/**
 * How the sign-in code arrives here: WhatsApp, or not at all yet. `none` while
 * loading, so the sign-in page errs towards "no code will reach you".
 */
export function useOtpChannel(): 'whatsapp' | 'none' {
  /* Plain state, not a query: the sign-in page stands outside the signed-in
     shell, and must work with nothing set up around it. */
  const [channel, setChannel] = useState<'whatsapp' | 'none'>('none');
  useEffect(() => {
    let alive = true;
    apiGet<{ data: { otpChannel?: 'whatsapp' | 'none' } }>('/v1/cityload/services')
      .then((result) => {
        if (alive) setChannel(result.data.otpChannel ?? 'none');
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  return channel;
}

export interface ServiceSwitchRow {
  readonly channel: ServiceChannel;
  readonly enabled: boolean;
  readonly reason: string | null;
  readonly changedAt: string;
  readonly changedByPhone: string | null;
  readonly changeCount: number;
  /** Switching this on is a regulatory act, so the console says so out loud. */
  readonly licenceGated: boolean;
}

export interface ServiceSwitchChange {
  readonly channel: ServiceChannel;
  readonly wasEnabled: boolean | null;
  readonly nowEnabled: boolean;
  readonly reason: string | null;
  readonly changedAt: string;
  readonly changedByPhone: string | null;
}

export function useServiceSwitches(): UseQueryResult<{
  readonly rows: readonly ServiceSwitchRow[];
  readonly history: readonly ServiceSwitchChange[];
}> {
  return useQuery({
    queryKey: [...KEY, 'service-switches'],
    queryFn: async () => {
      const result = await apiGet<
        Envelope<ServiceSwitchRow[]> & { meta: { history: ServiceSwitchChange[] } }
      >('/v1/cityload/admin/services');
      return { rows: result.data, history: result.meta.history };
    },
  });
}

export function useThrowServiceSwitch(): ReturnType<
  typeof useMutation<
    { channel: string; enabled: boolean },
    Error,
    { channel: ServiceChannel; enabled: boolean; reason: string }
  >
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ channel, ...body }) => {
      const result = await request<Envelope<{ channel: string; enabled: boolean }>>(
        `/v1/cityload/admin/services/${channel}`,
        { method: 'POST', body },
      );
      return result.data.data;
    },
    /* The WHOLE TeraAgo key, not just this row. A service closing changes the
       machine catalogue, the booking screens and the dashboard's totals; a
       console still offering cab bookings after rides were shut is the exact
       failure this feature exists to prevent. */
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

/* ==========================================================================
   EVERY CHARGE ON A JOB THAT IS NOT THE WORK

   The rate card prices an hour of crane time. These are the lines around it:
   the trailer both ways, the rigging, a night rate, loading hands, a toll, a
   cancellation fee, GST. They were columns and could not grow; M086 made them
   rows, and this is how an administrator edits them.
   ========================================================================== */

export type ChargeKind =
  'flat' | 'per_km' | 'per_hour' | 'per_ton' | 'per_day' | 'per_litre' | 'pct_of_taxable';
export type ChargeWhen = 'always' | 'optional' | 'on_cancel';

export interface ChargeRuleRow {
  readonly id: string;
  readonly code: string;
  readonly label: string;
  readonly note: string | null;
  readonly kind: ChargeKind;
  readonly applies: ChargeWhen;
  readonly amountPaise: number;
  readonly pct: number | null;
  readonly freeUnits: number;
  readonly maxPaise: number;
  readonly appliesFrom: string | null;
  readonly appliesTo: string | null;
  readonly commissionable: boolean;
  readonly taxable: boolean;
  readonly operatorSettable: boolean;
  readonly minPaise: number | null;
  readonly maxBandPaise: number | null;
  readonly sortOrder: number;
  readonly state: 'active' | 'retired' | 'proposed' | 'rejected';
  readonly class: string | null;
  readonly classLabel: string | null;
  readonly areaId: string | null;
  readonly areaName: string | null;
  /** What this rule has actually charged: is it working, or forgotten? */
  readonly timesCharged: number;
  readonly updatedAt: string;
}

export function useChargeRules(): UseQueryResult<readonly ChargeRuleRow[]> {
  return useQuery({
    queryKey: [...KEY, 'charge-rules'],
    queryFn: async () =>
      (await apiGet<Envelope<ChargeRuleRow[]>>('/v1/cityload/admin/charge-rules')).data,
  });
}

/** Amounts go over the wire in RUPEES, because that is what an admin types. */
export interface ChargeRuleInput {
  readonly code: string;
  readonly label: string;
  readonly note?: string;
  readonly kind: ChargeKind;
  readonly applies?: ChargeWhen;
  readonly amount?: number;
  readonly pct?: number;
  readonly freeUnits?: number;
  readonly maxAmount?: number;
  readonly appliesFrom?: string;
  readonly appliesTo?: string;
  readonly commissionable?: boolean;
  readonly taxable?: boolean;
  readonly operatorSettable?: boolean;
  readonly minAmount?: number;
  readonly maxBand?: number;
  readonly sortOrder?: number;
  readonly class?: string | null;
  readonly areaId?: string | null;
}

export function useAddChargeRule(): ReturnType<
  typeof useMutation<{ id: string }, Error, ChargeRuleInput>
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const result = await request<Envelope<{ id: string }>>('/v1/cityload/admin/charge-rules', {
        method: 'POST',
        body,
      });
      return result.data.data;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

export function useEditChargeRule(): ReturnType<
  typeof useMutation<
    { id: string },
    Error,
    { id: string } & (Partial<ChargeRuleInput> | { retire: true })
  >
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }) => {
      const result = await request<Envelope<{ id: string }>>(
        `/v1/cityload/admin/charge-rules/${id}`,
        { method: 'POST', body },
      );
      return result.data.data;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}
